import React from 'react';

/**
 * 12-hour time input with AM/PM — platform-independent.
 *
 * Replaces native `<input type="time">` which renders in 24h or 12h
 * depending on the OS locale. On Windows Electron, the system locale
 * often defaults to 24h even for Indian English users, while Chrome
 * on the same machine shows 12h. This component always shows 12h
 * with an explicit AM/PM selector.
 *
 * Props:
 *   value    – string in "HH:mm" (24h) format, e.g. "14:30"
 *   onChange – callback receiving "HH:mm" (24h) string
 *   style    – optional wrapper style override
 */
const TimeInput12h = ({ value, onChange, style }) => {
  // Parse "HH:mm" → { hour12, minute, period }
  const parse = (v) => {
    const [h, m] = (v || '00:00').split(':').map(Number);
    return {
      hour12: h === 0 ? 12 : h > 12 ? h - 12 : h,
      minute: isNaN(m) ? 0 : m,
      period: h >= 12 ? 'PM' : 'AM',
    };
  };

  // { hour12, minute, period } → "HH:mm"
  const to24 = (h12, min, period) => {
    let h24 = h12;
    if (period === 'AM' && h12 === 12) h24 = 0;
    if (period === 'PM' && h12 !== 12) h24 = h12 + 12;
    return `${String(h24).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  };

  const { hour12, minute, period } = parse(value);

  const handleChange = (field, val) => {
    let h = hour12, m = minute, p = period;
    if (field === 'hour') h = Number(val);
    if (field === 'minute') m = Number(val);
    if (field === 'period') p = val;
    onChange(to24(h, m, p));
  };

  const selectStyle = {
    padding: '8px 4px',
    borderRadius: '6px',
    border: '1px solid #E2E8F0',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#0F172A',
    background: '#FFFFFF',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    textAlign: 'center',
    minWidth: '44px',
  };

  const periodStyle = {
    ...selectStyle,
    fontWeight: '600',
    color: '#FF6961',
    minWidth: '52px',
  };

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '2px',
      padding: '2px 8px',
      borderRadius: '8px',
      border: '1px solid #E2E8F0',
      background: '#FFFFFF',
      ...style,
    }}>
      <select
        value={hour12}
        onChange={(e) => handleChange('hour', e.target.value)}
        style={selectStyle}
      >
        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>

      <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748B' }}>:</span>

      <select
        value={minute}
        onChange={(e) => handleChange('minute', e.target.value)}
        style={selectStyle}
      >
        {Array.from({ length: 60 }, (_, i) => (
          <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
        ))}
      </select>

      <select
        value={period}
        onChange={(e) => handleChange('period', e.target.value)}
        style={periodStyle}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

export default TimeInput12h;
